import lazy from '@/util/lazy-val';

export function distinctBy<K extends string | number | symbol>(key: K) {
  return <T extends { [k in K]: any }>(as: T[]) => as.filter((a, index, self) => self.findIndex((b) => b[key] === a[key]) === index);
}

export function distinct<T>(as: T[]): T[] {
  return as.filter((a, index, self) => self.findIndex((b) => b === a) === index);
}
export function distinctByFn<T, K extends string | number | symbol>(as: ReadonlyArray<T>, key: (t: T) => K): ReadonlyArray<T> {
  const seen = new Set<K>();
  return as.filter((a) => {
    const k = key(a);
    if (seen.has(k)) {
      return false;
    }
    seen.add(k);
    return true;
  });
}

export function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

type ArrayDimensions<A extends any[], T> =
  A extends [] ? [] :
  A extends [any] ? T[] :
  ((...x: A) => any) extends ((x: any, ...y: infer B) => any) ? Array<ArrayDimensions<B, T>> :
  never;

export function createArray<N extends number[]>(...sizes: N) {
  return <T>(f: (...ns: N) => T): ArrayDimensions<N, T> => {
    if (sizes.length === 0) {
      return [] as ArrayDimensions<N, T>;
    }
    const result: any[] = [];
    for (let i = 0; i < sizes[0]; i++) {
      if (sizes.length === 1) {
        result.push((f as any)(i));
      } else {
        result.push(createArray(...sizes.slice(1))((f as any).bind(null, i)));
      }
    }
    return result as ArrayDimensions<N, T>;
  };
}

export function orderBy<T>(...keys: Array<(t: T) => any>): (a: T, b: T) => number;
export function orderBy<T, K extends keyof T>(...keys: K[]): (a: T, b: T) => number;
export function orderBy<T, K extends keyof T>(...keys: Array<(t: T) => any | K>): (a: T, b: T) => number {
  return (a: T, b: T) => {
    for (const key of keys) {
      if (key instanceof Function) {
        const ka = key(a);
        const kb = key(b);
        if (ka < kb) {
          return -1;
        } if (kb < ka) {
          return 1;
        }
      } else {
        if (a[key] < b[key]) {
          return -1;
        } if (b[key] < a[key]) {
          return 1;
        }
      }
    }
    return 0;
  };
}

export function groupBy<T, K>(ts: ReadonlyArray<T>, key: (t: T) => K): ReadonlyMap<K, ReadonlyArray<T>>;
export function groupBy<T, K extends keyof T>(ts: ReadonlyArray<T>, key: K): ReadonlyMap<T[K], ReadonlyArray<T>>;
export function groupBy<T, K>(ts: ReadonlyArray<T>, key: (t: T) => K | K) {
  const result = new Map<any, T[]>();
  const f = key instanceof Function ? key : (t: T) => t[key];
  for (const t of ts) {
    const k = f(t);
    const a = result.get(k);
    if (a) {
      a.push(t);
    } else {
      result.set(k, [t]);
    }
  }
  return result;
}

export function uniqueIndex<T, K>(ts: ReadonlyArray<T>, key: (t: T) => K): ReadonlyMap<K, T>;
export function uniqueIndex<T, K extends keyof T>(ts: ReadonlyArray<T>, key: K): ReadonlyMap<T[K], T>;
export function uniqueIndex<T, K>(ts: ReadonlyArray<T>, key: (t: T) => K | K) {
  const result = new Map<any, T>();
  const f = key instanceof Function ? key : (t: T) => t[key];
  for (const t of ts) {
    const k = f(t);
    const a = result.get(k);
    if (a) {
      throw Error('Duplicate keys in unique index.');
    } else {
      result.set(k, t);
    }
  }
  return result;
}

export type IndexedArray<K, V> = ReadonlyArray<V> & {
  get(k: K): ReadonlyArray<V>;
  indexKeys(): ReadonlyArray<K>;
};
export function indexed<K, V>(vs: V[], key: (v: V) => K): IndexedArray<K, V> {
  const index = lazy(() => groupBy(vs, key));
  const arr = (vs || []) as any;
  arr.get = (k: K) => index().get(k) || [];
  arr.indexKeys = () => vs.map(key);
  return arr;
}

export type UniquelyIndexedArray<K, V> = ReadonlyArray<V> & {
  get(k: K): V | undefined;
  indexKeys(): ReadonlyArray<K>;
};
export function uniquelyIndexed<K, V>(vs: readonly V[], key: (v: V) => K): UniquelyIndexedArray<K, V> {
  const index = lazy(() => uniqueIndex(vs, key));
  const arr = (vs || []) as any;
  arr.get = (k: K) => index().get(k);
  arr.indexKeys = () => vs.map(key);
  return arr;
}

export function sortBy<T>(weight: (t: T) => number, desc?: boolean): (arr: T[]) => T[] {
  return (arr) => arr.sort((a, b) => (desc ? weight(b) - weight(a) : weight(a) - weight(b)));
}

export function sortByExplicit<T, U>(by: (t: T) => U, order: U[]): (a: T, b: T) => number {
  return (a, b) => {
    const ai = order.indexOf(by(a));
    const bi = order.indexOf(by(b));
    if (ai < 0) {
      return 1;
    } if (bi < 0) {
      return -1;
    }
    return ai - bi;
  };
}

export function objectToArray<K extends string | number | symbol, T>(
  o: { [P in K]?: T },
): T[];
export function objectToArray<K extends string | number | symbol, T, U>(
  o: { [P in K]?: T },
  f: (k: K, v: T) => U,
): U[];
export function objectToArray<K extends string | number | symbol, T, U>(
  o: { [P in K]?: T },
  f?: (k: K, v: T) => U,
): U[] {
  const result: U[] = [];
  for (const k in o) {
    if (Object.prototype.hasOwnProperty.call(o, k)) {
      result.push(f ? f(k, o[k] as unknown as T) : o[k] as unknown as U);
    }
  }
  return result;
}

type Cartesian<T extends any[][]> = {
  [P in keyof T]: T[P] extends Array<infer U> ? U : never;
};
export function* cartesian<T extends any[][]>(...arr: T): Iterable<Cartesian<T>> {
  const lengths = arr.map((a) => a.length);
  const count = lengths.reduce((a, b) => a * b, 1);
  for (let i = 0; i < count; i++) {
    const row = new Array(arr.length);
    let mod = i;
    for (let j = row.length - 1; j >= 0; j--) {
      const n = mod % lengths[j];
      row[j] = arr[j][n];
      if (j > 0) {
        mod = ((mod - n) / lengths[j]);
      }
    }
    yield row as Cartesian<T>;
  }
}
